import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, SearchIcon } from "@heroicons/react/outline";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import RenameSignalDialog from "./RenameSignalDialog";
import SignalCard from "./SignalCard";

import { ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { GifType, Wrangler } from "@/components/ui/Wrangler";
import { useAuth } from "@/context/auth-context";
import { useConfirm } from "@/context/confirm-context";
import { useUser } from "@/context/user-context";
import { useDebounce } from "@/hooks";
import {
  Signal,
  buildSignalStatsById,
  useDeleteSignal,
  useEditSignal,
  useGetSignalStats,
  useGetSignals,
} from "@/queries/signals";

const SIGNALS_PER_PAGE = 20;

const SignalsList = () => {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const userToken = useAuth()?.userToken ?? "";
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const deleteSignal = useDeleteSignal();
  const editSignal = useEditSignal();
  const [signalToRename, setSignalToRename] = useState<Signal | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery]);

  const { data, isLoading } = useGetSignals({
    userToken,
    workspaceId,
    q: debouncedSearchQuery,
    page,
    perPage: SIGNALS_PER_PAGE,
  });

  const signals = data?.signals ?? [];
  const signalIds = signals.map(signal => signal.id);
  const {
    data: statsData,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
  } = useGetSignalStats({
    userToken,
    workspaceId,
    signalIds,
    enabled: !isLoading && signalIds.length > 0,
  });
  const statsById = buildSignalStatsById(statsData?.stats);
  const showStatsShimmer = isStatsLoading || (isStatsFetching && !statsData);

  const hasActiveSearch = Boolean(debouncedSearchQuery.trim());
  const hasNext = data?.has_next ?? false;
  const hasPrev = data?.has_prev ?? false;
  const totalPages = data?.pages ?? 0;
  const currentPage = data?.page ?? page;

  const handleRename = useCallback((signal: Signal) => {
    setSignalToRename(signal);
  }, []);

  const handleToggleEnabled = useCallback(
    async (signal: Signal) => {
      await editSignal.mutateAsync({
        workspaceId,
        signalId: signal.id,
        userToken,
        enabled: !signal.enabled,
        successMessage: signal.enabled ? "Signal disabled" : "Signal enabled",
      });
    },
    [editSignal, userToken, workspaceId],
  );

  const handleDelete = useCallback(
    async (signal: Signal) => {
      const confirmed = await confirm({
        title: `Delete signal: "${signal.name}"`,
        message: "This signal will be permanently deleted. This action cannot be undone.",
        confirmText: "Delete signal",
        cancelText: "Cancel",
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructive",
        exitButtonVariant: "outline",
        requireConfirmation: true,
        confirmationText: signal.name,
        confirmationPlaceholder: signal.name,
      });

      if (!confirmed) {
        return;
      }

      await deleteSignal.mutateAsync({
        workspaceId,
        signalId: signal.id,
        userToken,
      });
    },
    [confirm, deleteSignal, userToken, workspaceId],
  );

  let signalsContent = (
    <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2">
      {signals.map(signal => (
        <SignalCard
          key={signal.id}
          signal={signal}
          workspaceId={workspaceId}
          stats={statsById.get(signal.id)}
          isStatsLoading={showStatsShimmer}
          onRename={handleRename}
          onDelete={handleDelete}
          onToggleEnabled={handleToggleEnabled}
          isTogglingEnabled={editSignal.isPending}
        />
      ))}
    </div>
  );

  if (isLoading) {
    signalsContent = (
      <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2">
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-36 w-full" />
      </div>
    );
  } else if (signals.length === 0 && !hasActiveSearch) {
    signalsContent = (
      <div className="mt-12 flex flex-1 flex-col items-center justify-center px-8 text-center">
        <Wrangler variant={GifType.Searching} size="lg" hideText />
        <h3 className="mb-2 text-lg font-medium text-gray-900">No signals yet</h3>
        <p className="mb-6 text-gray-500">Create your first signal to get started.</p>
        <Button
          type="button"
          onClick={() => navigate(`/workspace/${workspaceId}/signals/new`)}
          variant="default"
          size="lg"
        >
          <div className="flex h-5 w-5 items-center justify-center rounded bg-white/20">
            <PlusIcon className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="ml-2">Create New Signal</span>
        </Button>
      </div>
    );
  } else if (signals.length === 0) {
    signalsContent = (
      <div className="mt-12 flex flex-1 flex-col items-center justify-center px-8 text-center">
        <Wrangler variant={GifType.Searching} size="lg" hideText />
        <h3 className="mb-2 text-lg font-medium text-gray-900">No signals found</h3>
        <p className="mb-6 text-gray-500">
          Try adjusting your search to find what you&apos;re looking for.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-1 py-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Signals</h2>
          <p className="mt-1 text-sm text-gray-500">Label requests with custom checks</p>
        </div>
        <Button type="button" onClick={() => navigate(`/workspace/${workspaceId}/signals/new`)}>
          <PlusIcon className="mr-2 h-4 w-4" />
          New signal
        </Button>
      </div>

      <div className="my-2 px-1">
        <div className="relative w-full">
          <SearchIcon className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
          <Input
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search signals..."
            className="box-border h-10 w-full border-0 bg-gray-100 pl-10 pr-4 text-sm shadow-none focus:border-0 focus:ring-0"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-2">{signalsContent}</div>

      {signals.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-1 py-3">
          <p className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasPrev || isLoading}
              onClick={() => setPage(current => Math.max(1, current - 1))}
            >
              <ChevronLeftIcon className="mr-1 h-4 w-4" />
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasNext || isLoading}
              onClick={() => setPage(current => current + 1)}
            >
              Next
              <ChevronRightIcon className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <RenameSignalDialog
        open={signalToRename !== null}
        onOpenChange={open => {
          if (!open) {
            setSignalToRename(null);
          }
        }}
        signal={signalToRename}
      />
    </div>
  );
};

export default SignalsList;
